import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { CurrentUserService } from '../../user/services';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { TransportType, TransportCategory } from '../../transport/models/transport-type';
import { TransportService } from '../../transport/services/transport.service';
import { ArtifactAssemblyTypes, AssemblyArtifact } from '../models/artifact';
import { DeveloperArtifactService } from '../services/developer-artifact.service';
import { DeveloperService } from '../services/developer.service';
import { GridService } from '../../shared/grid/grid.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoFormModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DeveloperArtifactAssemblyPickerComponent } from '../developer-artifacts/developer-artifact-assembly-picker.component';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'app-developer-transport-types',
    templateUrl: './developer-transport-types.component.html',
    styleUrls: ['./developer-transport-types.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoFormModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule, DeveloperArtifactAssemblyPickerComponent]
})
export class DeveloperTransportTypesComponent implements OnInit {

    hasCreatePerm = false;
    hasEditPerm = false;
    hasDeletePerm = false;
    hasShowLogPerm = false;

    transportTypes: TransportType[] = [];
    artifacts: AssemblyArtifact[] = [];

    constructor(
        private developerService: DeveloperService,
        private transportService: TransportService,
        private toastService: ToastService,
        private currentUserService: CurrentUserService,
        private loadingPanelService: LoadingPanelService,
        private developerArtifactService: DeveloperArtifactService,
        private gridService: GridService,
        public bizGridFiltervalues: BizGridFiltervalues) {

        this.allowUpdatingRow = this.allowUpdatingRow.bind(this);
        this.allowDeletingRow = this.allowDeletingRow.bind(this);
        this.allowShowLog = this.allowShowLog.bind(this);
        this.displayExprArtifact = this.displayExprArtifact.bind(this);
    }

    async ngOnInit() {
        this.artifacts = await this.developerArtifactService.getAssemblyArtifacts(true, [ArtifactAssemblyTypes.TypeTransportRetrieve, ArtifactAssemblyTypes.TypeTransportSend]);
        this.hasCreatePerm = this.currentUserService.hasPermission('Developer_Action_TransportType_Create');
        this.hasEditPerm = this.currentUserService.hasPermission('Developer_Action_TransportType_Edit');
        this.hasDeletePerm = this.currentUserService.hasPermission('Developer_Action_TransportType_Delete');
        this.hasShowLogPerm = this.currentUserService.hasPermission('Developer_Action_ShowLog');
        this.refresh();
    }

    allowUpdatingRow(options: { component: any; row: any; }): boolean {
        const item: TransportType = options?.row?.data;
        if (item) {
            return this.hasEditPerm && !item.IsSystemTransportType;
        }
        return false;
    }

    allowDeletingRow(options: { component: any; row: any; }): boolean {
        const item: TransportType = options?.row?.data;
        if (item) {
            return this.hasDeletePerm && !item.IsSystemTransportType;
        }
        return false;
    }

    allowShowLog(options: { component: any; row: any; }): boolean {
        const item: TransportType = options?.row?.data;
        if (item) {
            return this.hasShowLogPerm && !item.IsSystemTransportType;
        }
        return false;
    }

    displayExprArtifact(e: any) {
        // TODO: Find en bedre måde at håndtere dette, så det ikke er nødvendigt at loade alle artifacts...
        if (this.artifacts) {
            var artifact = this.artifacts.find(a => a.ArtifactId == e.value);
            if (artifact) {
                return `${artifact.Name} [${artifact.ClassName}] [${artifact.Filename}]`;
            }
        }
        return "Unknown...";
    }

    async refresh() {
        this.loadingPanelService.showLoadingPanelDirect('Loading transport types...');
        this.transportTypes = await this.transportService.getTransportTypes([TransportCategory.Outbox, TransportCategory.Polling, TransportCategory.Send]);
        this.loadingPanelService.hideLoadingPanel();
    }

    onInitNewRow(e: any): void {
        e.data = { Id: null, Name: '', Description: '', IsSystemTransportType: false } as TransportType;
    }

    onRowInserting = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as TransportType, item => this.saveTransportType(item));
    };

    onRowUpdating = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.key, ...e.newData } as TransportType, item => this.saveTransportType(item), e.newData);
    };

    onRowRemoving = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as TransportType, item => this.removeTransportType(item));
    };

    async saveTransportType(transportType: TransportType): Promise<TransportType> {
        const transportTypeExist = this.transportTypes.find(i => i.TransportKey == transportType.TransportKey && i.Id != transportType.Id);
        if (transportTypeExist != null) {
            throw new Error(`Another transport-type with that TransportKey already exists. Name of the existing type: ${transportTypeExist.Name}`);
        }
        
        const newItem = await this.developerService.saveTransportType(transportType);
        this.toastService.showToast('Transport type saved...', ToastType.success);
        return newItem;
    }

    async removeTransportType(transportType: TransportType) : Promise<TransportType> {
        await this.developerService.deleteTransportType(transportType.Id);
        this.toastService.showToast('Transport type deleted...', ToastType.success);
        return transportType;
    }

    async setArtifactId(artifactId, cellInfo: any) {
        cellInfo.setValue(artifactId || '');
        if (cellInfo.data.ArtifactId != artifactId && artifactId) { // Only check artifact if it has changed!
            try {
                this.loadingPanelService.showLoadingPanelDirect('Verifying artifact...');
                const itineraryStepInfo = await this.developerService.getTransportTypeInfo(artifactId);
                cellInfo.component.cellValue(cellInfo.row.rowIndex, 'TransportKey', itineraryStepInfo.TransportKey);
                cellInfo.component.cellValue(cellInfo.row.rowIndex, 'Type', itineraryStepInfo.TransportType);
            } finally {
                this.loadingPanelService.hideLoadingPanel();
            }
        }
    }

    customizeItem(item: any) {
        if (item.dataField == "IsSystemTransportType") { // Hide the 'Download' column when editing...
            item.visible = false;
        }
    }
}
